import React, { Component } from "react";
import { ReactCookieProps, withCookies } from "react-cookie";
import base64 from "base-64";
import { Form, Button } from "semantic-ui-react";
import PhoneInput, { CountryData } from "react-phone-input-2";

import PhoneNumber from "awesome-phonenumber";
import Erro404 from "../404";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import "react-phone-input-2/lib/style.css";
import { Props } from "../propTypes";
import { childFormComponent } from "./types";
import { customPhoneMasks, ObjectLiteral } from "../../constants";
import classnames from "classnames";
import { ResetForm } from "./ResetForm";
// Phone Form Component
/**
 * This component is used to input and store a user's personal details in MyElevation.
 *
 * We are using the Form and Button components from Semantic UI React Library.
 * For more information on these components can be found at https://react.semantic-ui.com/collections/form/
 * and https://react.semantic-ui.com/elements/button/
 *
 * For phone numbers, we are using the PhoneInput component for international phone number formatting
 * (https://www.npmjs.com/package/react-phone-input-2) and the PhoneNumber component to validate
 * (https://www.npmjs.com/package/awesome-phonenumber)
 *
 * React Cookie is being used to set a cookie on the user's browser for 30 minutes. More information about this
 * can be found at https://www.npmjs.com/package/react-cookie/
 */

type State = {
    valid: boolean;
    showValidation: boolean;
    validationText: string;
    formatData: ObjectLiteral | null;
    phoneInputContent: string;
};

class Phone extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    state = {
        valid: false,
        showValidation: false,
        validationText: "",
        formatData: null,
        phoneInputContent: "+1",
    };

    componentDidMount() {
        const { values } = this.props;

        if (
            values &&
            values.phoneConfirmationFormat &&
            values.phoneConfirmationFormat.countryCode &&
            values.phoneConfirmation
        ) {
            const pn = new PhoneNumber(
                values.phoneConfirmation.replace(
                    values.phoneConfirmationFormat.dialCode,
                    ""
                ),
                values.phoneConfirmationFormat.countryCode
            );
            const valid = pn.isValid() || false;

            this.setState({
                valid,
                formatData: values.phoneConfirmationFormat,
            });
        }
    }

    // Component Did Update Lifecycle Method
    /**
     * componentDidUpdate() runs immediately after the component updates.
     * This method does not run during the initial render.
     *
     * In this instance, we are validating the code entered by the user. If there is an error, we return a message.
     * If valid, we create a cookie for 30 minutes, so that if the user goes to the form again, they can skip this step.
     */
    componentDidUpdate(
        prevProps: Props & childFormComponent & ReactCookieProps
    ) {
        if (
            this.props.main.confirmationCodeData &&
            this.props.main.confirmationCodeData.status_code &&
            this.props.main.confirmationCodeData.status_code !== 200 &&
            prevProps.main.confirmationCodeData &&
            prevProps.main.confirmationCodeData.status_code !==
                this.props.main.confirmationCodeData.status_code
        ) {
            let errorText =
                "Error sending the confirmation code. Please try again later.";
            if (
                this.props.main.confirmationCodeData.message &&
                _.isArray(this.props.main.confirmationCodeData.message) &&
                this.props.main.confirmationCodeData.message[0] &&
                _.isObject(this.props.main.confirmationCodeData.message[0])
            ) {
                errorText =
                    this.props.main.confirmationCodeData &&
                    this.props.main.confirmationCodeData.messages &&
                    (
                        this.props.main.confirmationCodeData
                            .message as ObjectLiteral[]
                    )[0][
                        Object.keys(
                            this.props.main.confirmationCodeData.message[0]
                        )[0]
                    ];
            }

            const extraErrorData = {
                status_code: this.props.main.confirmationCodeData.status_code,
                other_validation_data: this.props.main.confirmationCodeData,
            };

            if (this.props.main.confirmationCodeData.origin !== "USER") {
                this.props.logError &&
                    this.props.logError(
                        errorText,
                        Sentry.Severity.Fatal,
                        extraErrorData
                    );
            }

            this.setState({
                showValidation: true,
                validationText: errorText,
            });
        }

        if (
            this.props.main.confirmationCodeData &&
            this.props.main.confirmationCodeData.status_code === 200 &&
            prevProps.main.confirmationCodeData &&
            prevProps.main.confirmationCodeData.status_code !==
                this.props.main.confirmationCodeData.status_code
        ) {
            this.props.nextStep();
        }

        if (
            this.props.main.token_error &&
            this.state.validationText !== this.props.main.token_error
        ) {
            this.setState({
                showValidation: true,
                validationText: this.props.main.token_error,
            });
        }
    }

    saveAndContinue = (e: any) => {
        e.preventDefault();
        if (this.state.valid) {
            const { cookies, values } = this.props;
            let content = "";

            if (cookies) {
                cookies.get(
                    "confirmation_" + this.props.registrationType.toLowerCase()
                );
            }

            let contentSplit: Array<any> | null = null;
            let isTheSamePhoneNumber = false;
            let decodedData;

            if (content && this.props.saveSession) {
                const contentDecode = base64.decode(content);
                contentSplit = contentDecode.split("+++");
            }

            if (content && contentSplit && contentSplit.length === 2) {
                if (contentSplit[0]) {
                    decodedData = JSON.parse(contentSplit[0]);

                    if (
                        decodedData &&
                        decodedData.type === "phone" &&
                        decodedData.data &&
                        decodedData.data.phoneNumber &&
                        decodedData.data.phoneNumber ===
                            values.phoneConfirmation
                    ) {
                        isTheSamePhoneNumber = true;
                    }
                }
            }

            if (
                content &&
                contentSplit &&
                contentSplit.length === 2 &&
                isTheSamePhoneNumber
            ) {
                this.props.handleChange("phoneConfirmationFormat")(
                    null,
                    decodedData.data.extraData
                );
                if (contentSplit[1]) {
                    const userData = JSON.parse(contentSplit[1]);
                    this.props.handleChange("fromSalesForce")(null, true);
                    this.props.actions.main.main_set_user_data(
                        JSON.parse(contentSplit[1])
                    );

                    if (
                        this.props.allowContactToUpdateSalesForceInfo &&
                        this.props.isContactAllowedToUpdateSalesForceInfo &&
                        this.props.isContactAllowedToUpdateSalesForceInfo(
                            JSON.parse(contentSplit[1])
                        ) &&
                        ((decodedData.type === "email" &&
                            decodedData.data.emailConfirmation !==
                                userData.email) ||
                            (decodedData.type === "phone" &&
                                decodedData.data.phoneNumber.replace(
                                    decodedData.data.extraData.dialCode,
                                    ""
                                ) !== userData.mobileNumber))
                    ) {
                        this.props.nextStep(14);
                    } else {
                        this.props.nextStep(6);
                    }
                } else {
                    this.props.nextStep(3);
                }
            } else {
                this.props.handleChange("phoneConfirmationFormat")(
                    null,
                    this.state.formatData
                );
                this.props.actions.main.main_send_confirmation_code({
                    dataToSend: { sms: true, number: values.phoneConfirmation },
                    origin: this.props.registrationType,
                    extraData: this.state.formatData || undefined,
                });
            }

            this.setState({
                showValidation: false,
            });
        } else {
            this.setState({
                showValidation: true,
                validationText: "Invalid phone number",
            });
        }
    };

    // Go to Email Function
    /**
     * goToEmail() sends the user to email if phoneOrEmail() calls for it.
     */
    goToEmail = (e: any) => {
        e.preventDefault();
        this.props.handleChange("phoneConfirmation")(null, "+1");
        this.props.phoneOrEmail && this.props.phoneOrEmail();
    };

    // Handle Input Change Function
    /**
     * handleInputChange() validates and stores the value of the phone number field to the state.
     */
    handleInputChange = (
        telNumber: any,
        data: CountryData,
        e,
        formattedPhoneNumber
    ) => {
        const pn = new PhoneNumber(formattedPhoneNumber);
        const valid = pn.isValid() || false;

        if (valid !== this.state.valid) {
            this.setState({
                valid,
                formatData: data,
                showValidation: false,
                validationText: "",
                phoneInputContent: telNumber,
            });
        } else {
            this.setState({
                phoneInputContent: telNumber,
            });
        }

        this.props.handleChange("phoneConfirmation")(null, telNumber);
    };

    render() {
        if (this.props.showError404) {
            return (
                <Erro404 containerClass="flex flex-justify-center flex-align-center flex-column m-b-spacer-xl" />
            );
        }

        const { values } = this.props;

        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-b-spacer-3xl">
                    <Form
                        className={`p-y-spacer-lg ec-background-white phone-number-form ${
                            this.state.showValidation ? "ec-error" : ""
                        }`}
                    >
                        <Form.Field
                            className={classnames(
                                "label-animation-input with-phone-field rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    filled: this.state.phoneInputContent,
                                }
                            )}
                        >
                            <label>Phone Number</label>
                            {/* Disabling autofill for Chrome: readonly and onfocus attribute */}
                            {/* Disabling autofill for Safari: autoComplete attribute */}
                            <PhoneInput
                                country="us"
                                preferredCountries={["us"]}
                                value={values.phoneConfirmation || ""}
                                inputClass={`full-width ${
                                    this.state.showValidation ? "ec-error" : ""
                                }`}
                                placeholder=""
                                inputProps={{
                                    id: "",
                                    autoFocus: false,
                                    autoComplete: "off",
                                }}
                                masks={customPhoneMasks}
                                onChange={this.handleInputChange}
                            />
                        </Form.Field>
                        {this.state.showValidation ? (
                            <small className="ec-primary-font ec-line-height">
                                {this.state.validationText}
                            </small>
                        ) : null}

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                id={`${this.props.tagPrefixId}-phone-next`}
                                className="full-width no-border text-white m-t-spacer-md p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius-sm"
                                disabled={
                                    !this.state.valid || this.props.isLoading
                                }
                                onClick={this.saveAndContinue}
                            >
                                Next
                            </Button>
                        )}
                    </Form>

                    <div className="m-t-spacer-lg text-center">
                        <small className="regular">
                            <span className="text-alt-dark-grey half-here">
                                Prefer to receive a email?
                            </span>{" "}
                            <Button
                                id={`${this.props.tagPrefixId}-phone-send-email`}
                                onClick={this.goToEmail}
                                className="ec-no-background no-border bold help-text hover-grow ec-border-radius"
                            >
                                Send email
                            </Button>
                        </small>
                    </div>
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-phone`}
                    cancelUrl={this.props.cancelUrl || ""}
                    showReset={false}
                />
            </>
        );
    }
}

export default withCookies(Phone);
